import {
  AttributeItem,
  AttributeList,
} from "@volca/ui/components/ui/attribute-list";
import { Stack } from "@volca/ui/components/ui/stack";
import { useReportCurrency } from "@/profitability/currency";
import { MissingFigure, showValue } from "@/profitability/missing-value";
import { ActualVsEstimate } from "@/profitability/records/estimate-variance";
import {
  rowVariance,
  withoutShownEstimates,
} from "@/profitability/records/estimates";
import { ObjectIcon } from "@/profitability/records/object-icon";
import type { RecordObject, RecordRef } from "@/profitability/records/objects";
import {
  findReferenced,
  referenceName,
  referenceRef,
} from "@/profitability/records/objects";
import { RecordChip } from "@/profitability/records/record-chip";
import { formatCell, isNumeric } from "@/profitability/records/table-values";
import { SourceButton } from "@/profitability/sources/source-button";
import { isSourced } from "@/profitability/sources/sourced";
import { type FigureGaps, needsOf } from "./figure-gaps";
import { isAbsent } from "./missing-fields";

/**
 * The record's attributes, label and value: the `only` ones in that order
 * (a group the script declared), or every one the report has.
 */
export const RecordDetails = ({
  object,
  objects,
  row,
  only,
  onOpenRecord,
  gaps,
  onShowSource,
}: {
  object: RecordObject;
  objects: RecordObject[];
  row: Record<string, string | number | boolean | null>;
  only?: string[];
  onOpenRecord: (ref: RecordRef) => void;
  /** The missing figures that wait on others, said beside their "Missing". */
  gaps?: FigureGaps | null;
  /** Opens the file a figure was read from; its button only on a sourced one. */
  onShowSource?: (column: string) => void;
}) => {
  const keys = object.dataset.key_columns ?? [];
  const currency = useReportCurrency();
  const columns = only
    ? only.flatMap((key) =>
        object.dataset.columns.filter((column) => column.key === key),
      )
    : // An estimate shows beside its actual, so it is not listed again.
      withoutShownEstimates(
        object.dataset.columns.filter(
          (column) => !keys.includes(column.key) && !column.flag,
        ),
      );
  return (
    <AttributeList className="grid-cols-[minmax(0,1fr)_auto] [&_dd]:text-end">
      {columns.map((column) => {
        const target = column.references
          ? objects.find((item) => item.dataset.id === column.references)
          : undefined;
        const value = row[column.key];
        const estimated = isNumeric(column)
          ? rowVariance(object.dataset.columns, column, row)
          : null;
        const content =
          target && value ? (
            <RecordChip
              object={target}
              name={referenceName(target, value)}
              row={findReferenced(target, value)}
              onOpen={() => onOpenRecord(referenceRef(target, value))}
            />
          ) : estimated ? (
            <ActualVsEstimate
              variance={estimated.variance}
              column={column}
              currency={currency}
            />
          ) : isNumeric(column) && isAbsent(object.dataset, row, column) ? (
            <MissingFigure
              source={column.source ?? null}
              needs={needsOf(gaps, column.key)}
            />
          ) : (
            showValue(formatCell(value, column, currency)) || undefined
          );
        return (
          <AttributeItem
            key={column.key}
            label={column.label ?? column.key}
            icon={target ? <ObjectIcon icon={target.icon} /> : undefined}
            className="group/source"
          >
            {onShowSource && isSourced(object.dataset, column) ? (
              <Stack
                direction="row"
                inline
                align="center"
                gap={1}
                className="max-w-full"
              >
                {/* The button before the value, so every value ends on one edge. */}
                <SourceButton
                  label={`Show where ${column.label ?? column.key} comes from`}
                  onClick={() => onShowSource(column.key)}
                  className="-my-1"
                />
                <span className="min-w-0 truncate">
                  {content ?? <span className="text-subtlest">—</span>}
                </span>
              </Stack>
            ) : (
              content
            )}
          </AttributeItem>
        );
      })}
    </AttributeList>
  );
};
